import * as React from 'react'
import { Query, QueryResult } from 'react-apollo'
import { TFunction, useTranslation } from 'react-i18next'
import uuid from 'uuid/v1'

import { LOCIZE_NAMESPACE } from 'main/common'
import Loader from 'main/components/common/Loader'
import { i18nKeyPrefix } from 'main/components/tour/i18n'
import { Modal } from 'main/ui'
import { UnreachableCaseError } from 'main/util/ts'
import { CredentialsFields } from '../../../gql/credentials'
import AnonymousContent from '../AnonymousContent/AnonymousContent'
import { track } from '../track'
import Content from './Content'
import { REQUEST_TOUR_MODAL_QUERY, RequestTourModal } from './gql'
import { Step } from './types'

const PRE = `${i18nKeyPrefix}-REQUEST-MODAL`

interface IProps {
  schoolId: number
  onAuth?: (credentials: CredentialsFields) => void
  onUpdate?: () => void
  onRequestClose: () => void
}

export default function RequestATourModal(props: IProps) {
  const { schoolId, onRequestClose } = props
  const { t } = useTranslation(LOCIZE_NAMESPACE)
  const [step, setStep] = React.useState<Step | null>(null)

  const [sessionId] = React.useState(uuid())
  const trackData = { schoolId, sessionId }
  React.useEffect(() => track('opened', trackData), [])

  return (
    <Query
      query={REQUEST_TOUR_MODAL_QUERY}
      variables={{ schoolId }}
      onCompleted={(data: RequestTourModal) => {
        setStep(initialStep(data))
      }}
    >
      {(result: QueryResult<RequestTourModal>) => {
        const { data, loading, error } = result
        if (loading || error || !data || !step) {
          return <LoadingModal {...props} />
        }

        if (!data.school) {
          throw new Error('No school')
        }

        if (step.__stepname === 'login' || step.__stepname === 'sign_up') {
          if (data.user) {
            return <LoadingModal {...props} />
          }

          return (
            <Modal
              googleOptimize={googleOptimize(step)}
              title={title(step, t)}
              onRequestClose={onRequestClose}
            >
              <AnonymousContent
                trackData={trackData}
                school={data.school}
                step={step}
                onAuth={credentials => handleAuth(props, credentials)}
                onChangeStep={setStep}
              />
            </Modal>
          )
        }

        return (
          <Modal title={title(step, t)} onRequestClose={onRequestClose}>
            <Content
              data={data}
              step={step}
              onChangeStep={setStep}
              trackData={trackData}
              onRequestClose={handleRequestClose}
            />
          </Modal>
        )
      }}
    </Query>
  )

  function handleRequestClose() {
    track('closed', trackData)
    onRequestClose()
  }
}

function LoadingModal(props: IProps) {
  const { onRequestClose } = props

  return (
    <Modal title="" onRequestClose={onRequestClose}>
      <Loader />
    </Modal>
  )
}

function handleAuth(props: IProps, credentials: CredentialsFields) {
  const { onAuth } = props

  if (onAuth) {
    onAuth(credentials)
  }
}

function initialStep(data: RequestTourModal): Step {
  const { tourRequests, user } = data

  if (!user) {
    return { __stepname: 'sign_up' }
  }

  if (tourRequests && tourRequests.length > 0) {
    return { __stepname: 'confirmation' }
  }

  return { __stepname: 'provide_availability' }
}

function googleOptimize(step: Step) {
  return step.__stepname === 'sign_up' ? { title: 'requestATourSignUpTitle' } : {}
}

function title(step: Step | null, t: TFunction) {
  if (!step) return ''

  switch (step.__stepname) {
    case 'login':
    case 'sign_up':
    case 'provide_availability':
      return t(`${PRE}-DEFAULT`)

    case 'review':
      return t(`${PRE}-REVIEW`)

    case 'confirmation':
      return t(`${PRE}-CONFIRMATION`)

    default:
      throw new UnreachableCaseError(step)
  }
}
